import React, { useRef, useEffect, useState } from 'react';
import { theme as T } from '../../theme';
import { PC_BASE, pcPaths } from '../productCatalog/paths';
import { CATERING_BASE, cateringPaths } from '../catering/paths';
import { giftCardPaths } from '../giftCard/paths';
import { integrationsPaths } from '../integrations/paths';
import { reportsPaths } from '../reports/paths';
import { useAppSelector } from '../../redux/hooks';
import { DISPLAY_BASE, displayPaths } from '../display/paths';
import { MEDIA_MANAGEMENT_BASE, mediaManagementPaths } from '../mediaManagement/paths';
import { CAMPAIGNS_BASE, campaignPaths } from '../campaigns/paths';
import { getOwnerLocationCount } from '../../context/OwnerViewContext';
import { Bone } from '../Shimmer';
import { SUPER_ROLES, LOYALTY_ALLOWED_ROLES } from '../../constants/roles';
import { CUSTOMERS_BASE, customerPaths } from '../customers/paths';

function pcPathActive(pathname: string, href: string) {
  return pathname.startsWith(href);
}

/** Flat report nav links — used when pathname + navigate are passed. */
const REPORT_NAV_LINKS = [
  { id: 'eod', label: 'EOD Reconciliation', icon: 'credit_card', path: '/reports/eod' },
  { id: 'labor', label: 'Labor', icon: 'schedule', path: '/reports/labor' },
  { id: 'checkin', label: 'Check-In Analytics', icon: 'how_to_reg', path: '/reports/checkin' },
  { id: 'reservation', label: 'Reservations', icon: 'event_available', path: '/reports/reservation' },
  { id: 'customers', label: 'Customers', icon: 'people', path: '/reports/customers' },
  { id: 'events', label: 'Events & Pre-Order', icon: 'celebration', path: '/reports/events' },
];

const Chev = ({ s = 10, c = T.t3, r = 0 }: { s?: number; c?: string; r?: number }) => (
  <svg width={s} height={s} viewBox="0 0 10 10" style={{ transform: `rotate(${r}deg)`, transition: 'transform 0.2s', flexShrink: 0 }}>
    <path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const MSIcon = ({ name, size = 18, color = 'inherit', style = {} }: { name: string; size?: number; color?: string; style?: React.CSSProperties }) => (
  <span
    className="material-symbols-rounded"
    style={{ fontSize: size, lineHeight: 1, color, userSelect: 'none', flexShrink: 0, ...style }}
  >
    {name}
  </span>
);

/**
 * Standalone Sidebar component.
 * When pathname + navigate are passed, builds full nav (Snapshot, Product + report groups) internally.
 * Otherwise uses navItems prop.
 * @param {Object} [props.pathname] - current path (e.g. from useLocation) — when set with navigate, builds default nav
 * @param {Function} [props.navigate] - router navigate — when set with pathname, builds default nav
 */
export default function Sidebar({ brand = {}, outlet, navItems: navItemsProp = [], user, pathname, navigate }: any) {
  const outletRef = useRef(null);
  const userMenuRef = useRef(null);
  const useBuiltNav = pathname != null && typeof navigate === 'function';
  const isSuperUser = SUPER_ROLES.includes(user?.role);
  const isLoyaltyAllowed = LOYALTY_ALLOWED_ROLES.includes(user?.role);
  // Owner Dashboard (desktop) is only available to super-users with more than one
  // outlet — scenario 1. Single-outlet super-users and non-super users land on snapshot.
  const showOwnerView = isSuperUser && (getOwnerLocationCount() ?? 0) > 1;

  const reportPaths = ['/reports', '/snapshot', '/eod', '/checkin', '/reservation', '/customers', '/events'];
  // Customer Management owns the /customers/* namespace — exclude it from report-path detection
  // so the Reports group doesn't activate/expand when viewing the Customers module.
  const isCustomersPath = pathname === CUSTOMERS_BASE || pathname.startsWith(`${CUSTOMERS_BASE}/`);
  const isReportPath = (reportPaths.some((p) => pathname.startsWith(p)) || pathname === '/product') && !isCustomersPath;
  const isDocsPath = pathname === '/docs';

  const DOC_SECTIONS = ['overview', 'filters', 'snapshot', 'sales', 'product', 'checkin', 'eod', 'reservation', 'customers', 'events'];
  const [activeDocSection, setActiveDocSection] = useState('overview');

  useEffect(() => {
    if (!isDocsPath) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveDocSection(entry.target.id.replace('section-', ''));
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    // Observe after a short delay to ensure the page has rendered
    const timer = setTimeout(() => {
      DOC_SECTIONS.forEach((id) => {
        const el = document.getElementById(`section-${id}`);
        if (el) observer.observe(el);
      });
    }, 300);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [isDocsPath]);

  const isProductCatalogPath = pathname.startsWith(PC_BASE);
  const isDisplayPath = pathname.startsWith(DISPLAY_BASE);
  const isMediaManagementPath = pathname.startsWith(MEDIA_MANAGEMENT_BASE);

  const isCateringPath = pathname.startsWith(CATERING_BASE);
  const showCatering = outlet?.isCateringEnabled === true;
  
  const loyaltyPaths = ['/loyalty'];
  const isLoyaltyPath = loyaltyPaths.some((p) => pathname.startsWith(p));

  const loyaltyProgram = useAppSelector((s) => s.loyaltyProgram.program);
  const loyaltyProgramIsLive = loyaltyProgram != null;

  const loyaltyGroupChildren = [
    { type: 'link', id: 'loyalty-program', label: 'Program', icon: 'settings', active: pathname === '/loyalty' || pathname === '/loyalty/' || pathname.startsWith('/loyalty/program'), onClick: () => navigate('/loyalty/program') },
    { type: 'link', id: 'loyalty-rewards', label: 'Rewards', icon: 'card_giftcard', active: pathname.startsWith('/loyalty/rewards') || pathname.startsWith('/loyalty/automated-rewards'), onClick: () => navigate('/loyalty/rewards'), disabled: !loyaltyProgramIsLive },
    { type: 'link', id: 'loyalty-members', label: 'Members', icon: 'group', active: pathname.startsWith('/loyalty/members'), onClick: () => navigate('/loyalty/members'), disabled: !loyaltyProgramIsLive },
    { type: 'link', id: 'loyalty-notifications', label: 'Notifications', icon: 'notifications', active: pathname.startsWith('/loyalty/notifications'), onClick: () => navigate('/loyalty/notifications'), disabled: !loyaltyProgramIsLive },
  ];
  const loyaltyGroupActive = loyaltyGroupChildren.some((c) => c.active);

  const isReportsRoot = pathname === '/reports' || pathname === '/reports/';
  // Campaigns sit behind a per-brand flag (NFR-05); the group stays visible until settings say otherwise.
  const isCampaignsPath = pathname.startsWith(CAMPAIGNS_BASE);
  const campaignSettings = useAppSelector((s) => s.campaigns.settings);
  const showCampaigns = campaignSettings ? campaignSettings.featureEnabled : true;
  const reportsGroupChildren = [
    { type: 'link', id: 'snapshot', label: 'Snapshot', icon: 'flash_on', active: pathname === '/reports/snapshot' || isReportsRoot, onClick: () => navigate('/reports/snapshot') },
    // { type: 'link', id: 'snapshot-perf', label: 'Snapshot Perf', icon: 'speed', active: pathname === '/reports/snapshot-perf', onClick: () => navigate('/reports/snapshot-perf') },
    // { type: 'link', id: 'clickhouse-apis', label: 'CH API Dashboard', icon: 'api', active: pathname === '/reports/clickhouse-apis', onClick: () => navigate('/reports/clickhouse-apis') },
    { type: 'link', id: 'sales', label: 'Sales', icon: 'bar_chart', active: pathname === '/reports/sales' || pathname.startsWith('/reports/sales/'), onClick: () => navigate('/reports/sales') },
    { type: 'link', id: 'product', label: 'Product', icon: 'restaurant_menu', active: pathname === '/reports/product' || pathname.startsWith('/reports/product/'), onClick: () => navigate('/reports/product') },
    ...[...REPORT_NAV_LINKS, ...(showCampaigns ? [{ id: 'campaigns-report', label: 'Campaigns', icon: 'campaign', path: campaignPaths.performanceReport }] : [])].map((link) => ({
      type: 'link',
      id: link.id,
      label: link.label,
      icon: link.icon,
      active: pathname === link.path || pathname.startsWith(`${link.path}/`),
      onClick: () => navigate(link.path),
    })),
    ...(isSuperUser
      ? [{
          type: 'link',
          id: 'reports-reviews',
          label: 'Guest Reviews',
          icon: 'rate_review',
          active: pathname === reportsPaths.reviews,
          onClick: () => navigate(reportsPaths.reviews),
        }]
      : []),
  ];
  const reportsGroupActive = reportsGroupChildren.some((c) => c.active);

  const cateringGroupChildren = [
    { type: 'link', id: 'catering-leads', label: 'Leads', icon: 'contact_phone', active: pathname.startsWith(cateringPaths.leads), onClick: () => navigate(cateringPaths.leads) },
    { type: 'link', id: 'catering-estimates', label: 'Estimates', icon: 'request_quote', active: pathname.startsWith(cateringPaths.estimates), onClick: () => navigate(cateringPaths.estimates) },
    { type: 'link', id: 'catering-orders', label: 'Orders', icon: 'receipt_long', active: pathname === cateringPaths.orders || (pathname.startsWith(cateringPaths.orders) && !pathname.startsWith(cateringPaths.prepList) && !pathname.startsWith(cateringPaths.recurringOrders)), onClick: () => navigate(cateringPaths.orders) },
    // { type: 'link', id: 'catering-prep-list', label: 'Prep List', icon: 'checklist', active: pathname.startsWith(cateringPaths.prepList), onClick: () => navigate(cateringPaths.prepList) },
    { type: 'link', id: 'catering-invoices', label: 'Invoices', icon: 'receipt', active: pathname.startsWith(cateringPaths.invoices), onClick: () => navigate(cateringPaths.invoices) },
    { type: 'link', id: 'catering-customers', label: 'Customers', icon: 'people', active: pathname.startsWith(cateringPaths.customers), onClick: () => navigate(cateringPaths.customers) },
    { type: 'link', id: 'catering-packages', label: 'Packages', icon: 'inventory_2', active: pathname.startsWith(cateringPaths.packages), onClick: () => navigate(cateringPaths.packages) },
    { type: 'link', id: 'catering-settings', label: 'Settings', icon: 'settings', active: pathname.startsWith(cateringPaths.settings) || pathname.startsWith(cateringPaths.emailTemplates), onClick: () => navigate(cateringPaths.settings) },
  ];
  const cateringGroupActive = isCateringPath || cateringGroupChildren.some((c) => c.active);

  const isCampaignBuilderPath = isCampaignsPath && !pathname.startsWith(campaignPaths.automations) && /\/(create|edit|report)$/.test(pathname);
  const isCustomerSegmentsPath = pathname.startsWith(customerPaths.segments);
  const campaignsGroupChildren = [
    { type: 'link', id: 'campaigns-list', label: 'Campaigns', icon: 'campaign', active: pathname.startsWith(campaignPaths.list) || (isCampaignBuilderPath && !pathname.startsWith(campaignPaths.sources)), onClick: () => navigate(campaignPaths.list) },
    { type: 'link', id: 'campaigns-automations', label: 'Automations', icon: 'autorenew', active: pathname.startsWith(campaignPaths.automations), onClick: () => navigate(campaignPaths.automations) },
    { type: 'link', id: 'campaigns-customers', label: 'Customers', icon: 'group', active: isCustomersPath && !isCustomerSegmentsPath, onClick: () => navigate(customerPaths.directory) },
    { type: 'link', id: 'campaigns-segments', label: 'Segments', icon: 'filter_alt', active: isCustomerSegmentsPath, onClick: () => navigate(customerPaths.segments) },
    { type: 'link', id: 'campaigns-sources', label: 'Sources', icon: 'share', active: pathname.startsWith(campaignPaths.sources), onClick: () => navigate(campaignPaths.sources) },
    { type: 'link', id: 'campaigns-opt-outs', label: 'Opt-out queue', icon: 'mark_chat_unread', active: pathname.startsWith(campaignPaths.optOuts), onClick: () => navigate(campaignPaths.optOuts) },
    { type: 'link', id: 'campaigns-settings', label: 'Settings', icon: 'settings', active: pathname.startsWith(campaignPaths.settings), onClick: () => navigate(campaignPaths.settings) },
  ];
  // Customers sits inside the Campaigns group; with Campaigns switched off it is a link of its own again.
  const isCampaignsGroupPath = isCampaignsPath || (showCampaigns && isCustomersPath);
  const campaignsGroupActive = isCampaignsGroupPath;

  const productCatalogGroupChildren = [
    { type: 'link', id: 'pc-availability', label: 'Control Center', icon: 'dashboard', active: pcPathActive(pathname, pcPaths.availability), onClick: () => navigate(pcPaths.availability) },
    { type: 'link', id: 'pc-items', label: 'Items', icon: 'inventory_2', active: pcPathActive(pathname, pcPaths.items), onClick: () => navigate(pcPaths.items) },
    // { type: 'link', id: 'pc-menus', label: 'Menus', icon: 'restaurant_menu', active: pcPathActive(pathname, pcPaths.menus), onClick: () => navigate(pcPaths.menus) },
    { type: 'link', id: 'pc-categories', label: 'Categories', icon: 'category', active: pcPathActive(pathname, pcPaths.categories), onClick: () => navigate(pcPaths.categories) },
    { type: 'link', id: 'pc-modifiers', label: 'Modifiers', icon: 'tune', active: pcPathActive(pathname, pcPaths.modifiers), onClick: () => navigate(pcPaths.modifiers) },
    // { type: 'link', id: 'pc-variations', label: 'Variations', icon: 'checklist', active: pcPathActive(pathname, pcPaths.variations), onClick: () => navigate(pcPaths.variations) },
    // { type: 'link', id: 'pc-combos', label: 'Combos', icon: 'fastfood', active: pcPathActive(pathname, pcPaths.combos), onClick: () => navigate(pcPaths.combos) },
    // { type: 'link', id: 'pc-set-menus', label: 'Set Menus', icon: 'restaurant', active: pcPathActive(pathname, pcPaths.setMenus), onClick: () => navigate(pcPaths.setMenus) },
    { type: 'link', id: 'pc-kitchen-stations', label: 'Kitchen Stations', icon: 'countertops', active: pcPathActive(pathname, pcPaths.kitchenStations), onClick: () => navigate(pcPaths.kitchenStations) },
    { type: 'link', id: 'pc-sessions', label: 'Sessions', icon: 'schedule', active: pcPathActive(pathname, pcPaths.sessions), onClick: () => navigate(pcPaths.sessions) },
    { type: 'link', id: 'pc-sync-history', label: 'Sync History', icon: 'sync', active: pcPathActive(pathname, pcPaths.syncHistory), onClick: () => navigate(pcPaths.syncHistory) },
  ];
  const productCatalogGroupActive = productCatalogGroupChildren.some((c) => c.active);

  const isMediaLibraryPath = pathname.startsWith(displayPaths.mediaLibrary);
  const isDisplayListPath = isDisplayPath && !isMediaLibraryPath;
  const displayGroupChildren = [
    { type: 'link', id: 'display-media-library', label: 'Media Library', icon: 'perm_media', active: isMediaLibraryPath, onClick: () => navigate(displayPaths.mediaLibrary) },
    { type: 'link', id: 'display-list', label: 'Display List', icon: 'dashboard', active: isDisplayListPath, onClick: () => navigate(displayPaths.root) },
  ];
  const displayGroupActive = isDisplayPath;

  const mediaManagementGroupChildren = [
    { type: 'link', id: 'media-location', label: 'Pages', icon: 'storefront', active: pathname.startsWith(mediaManagementPaths.location), onClick: () => navigate(mediaManagementPaths.location) },
    { type: 'link', id: 'media-items', label: 'Items', icon: 'inventory_2', active: pathname.startsWith(mediaManagementPaths.items), onClick: () => navigate(mediaManagementPaths.items) },
  ];

  const scrollToDocSection = (sectionId: string) => {
    if (pathname !== '/docs') {
      navigate('/docs');
      setTimeout(() => document.getElementById(`section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } else {
      document.getElementById(`section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const docsGroupChildren = [
    { type: 'link', id: 'docs-overview', label: 'Getting Started', icon: 'rocket_launch', active: activeDocSection === 'overview', onClick: () => scrollToDocSection('overview') },
    { type: 'link', id: 'docs-filters', label: 'Date Filters', icon: 'date_range', active: activeDocSection === 'filters', onClick: () => scrollToDocSection('filters') },
    { type: 'link', id: 'docs-snapshot', label: 'Snapshot', icon: 'flash_on', active: activeDocSection === 'snapshot', onClick: () => scrollToDocSection('snapshot') },
    { type: 'link', id: 'docs-sales', label: 'Sales', icon: 'bar_chart', active: activeDocSection === 'sales', onClick: () => scrollToDocSection('sales') },
    { type: 'link', id: 'docs-product', label: 'Product & Menu', icon: 'restaurant_menu', active: activeDocSection === 'product', onClick: () => scrollToDocSection('product') },
    { type: 'link', id: 'docs-checkin', label: 'Check-In', icon: 'how_to_reg', active: activeDocSection === 'checkin', onClick: () => scrollToDocSection('checkin') },
    { type: 'link', id: 'docs-eod', label: 'EOD Summary', icon: 'credit_card', active: activeDocSection === 'eod', onClick: () => scrollToDocSection('eod') },
    { type: 'link', id: 'docs-reservation', label: 'Reservations', icon: 'event_available', active: activeDocSection === 'reservation', onClick: () => scrollToDocSection('reservation') },
    { type: 'link', id: 'docs-customers', label: 'Customers', icon: 'people', active: activeDocSection === 'customers', onClick: () => scrollToDocSection('customers') },
    { type: 'link', id: 'docs-events', label: 'Events & Pre-Orders', icon: 'celebration', active: activeDocSection === 'events', onClick: () => scrollToDocSection('events') },
  ];
  const docsGroupActive = isDocsPath;

  const ownerViewLink = {
    type: 'link',
    id: 'owner-view',
    label: 'Owner Dashboard',
    icon: 'domain',
    active: pathname === '/owner-view',
    onClick: () => navigate('/owner-view'),
  };

  const builtNavItems = useBuiltNav
    ? isDocsPath
      ? [
          {
            type: 'group',
            id: 'docs',
            label: 'Docs',
            icon: 'menu_book',
            active: docsGroupActive,
            children: docsGroupChildren,
          },
        ]
      : [
          ...(showOwnerView ? [ownerViewLink] : []),
          // owner-view hidden from desktop sidebar
          {
            type: 'link',
            id: 'employees',
            label: 'Employees',
            icon: 'badge',
            active: pathname.includes('employees'),
            onClick: () => navigate('/employees'),
          },
          {
            type: 'group',
            id: 'reports',
            label: 'Reports',
            icon: 'bar_chart',
            active: reportsGroupActive,
            children: reportsGroupChildren,
          },
            ...(showCatering ? [{
            type: 'group',
            id: 'catering',
            label: 'Catering',
            icon: 'lunch_dining',
            active: cateringGroupActive,
            children: cateringGroupChildren,
          }] : []),
          {
            type: 'link',
            id: 'gift-card',
            label: 'Gift Card',
            icon: 'card_giftcard',
            active: pathname.startsWith(giftCardPaths.hub),
            onClick: () => navigate(giftCardPaths.hub),
          },
        {
          type: 'group',
          id: 'productCatalog',
          label: 'Product Catalog',
          icon: 'storefront',
          active: productCatalogGroupActive,
          children: productCatalogGroupChildren,
        },
        {
          type: 'group',
          id: 'display',
          label: 'Display',
          icon: 'tv',
          active: displayGroupActive,
          children: displayGroupChildren,
        },
        {
          type: 'link',
          id: 'offers',
          label: 'Offers',
          icon: 'badge',
          active: pathname.includes('offers'),
          onClick: () => navigate('/offers', { state: { resetOffersTab: true, ts: Date.now() } }),
        },
        {
          type: 'group',
          id: 'mediaManagement',
          label: 'Kiosk Media',
          icon: 'perm_media',
          active: isMediaManagementPath,
          children: mediaManagementGroupChildren,
        },
        // Loyalty — restricted roles see a plain link (no dropdown)
        isLoyaltyAllowed
          ? {
              type: 'group',
              id: 'loyalty',
              label: 'Loyalty',
              icon: 'loyalty',
              active: loyaltyGroupActive,
              children: loyaltyGroupChildren,
            }
          : {
              type: 'link',
              id: 'loyalty',
              label: 'Loyalty',
              icon: 'loyalty',
              active: isLoyaltyPath,
              onClick: () => navigate('/loyalty'),
            },
        ...(showCampaigns ? [] : [{
          type: 'link',
          id: 'customers',
          label: 'Customers',
          icon: 'group',
          active: isCustomersPath,
          onClick: () => navigate(customerPaths.directory),
        }]),
        ...(isSuperUser
          ? [{
              type: 'link',
              id: 'integrations',
              label: 'Integrations',
              icon: 'extension',
              active: pathname.startsWith(integrationsPaths.hub),
              onClick: () => navigate(integrationsPaths.hub),
            }]
          : []),
        ...(showCampaigns ? [{
          type: 'group',
          id: 'campaigns',
          label: 'Campaigns',
          icon: 'campaign',
          active: campaignsGroupActive,
          children: campaignsGroupChildren,
        }] : []),
      ]
    : [];

  const navItems = useBuiltNav ? builtNavItems : navItemsProp;

  const allGroupsClosed = { reports: false, loyalty: false, productCatalog: false, docs: false, display: false, catering: false, mediaManagement: false, campaigns: false };
  const [groupExpanded, setGroupExpanded] = useState(() => {
    if (isProductCatalogPath) return { ...allGroupsClosed, productCatalog: true };
    if (isMediaManagementPath) return { ...allGroupsClosed, mediaManagement: true };
    if (isCateringPath) return { ...allGroupsClosed, catering: true };
    if (isCampaignsGroupPath) return { ...allGroupsClosed, campaigns: true };
    if (isLoyaltyPath) return { ...allGroupsClosed, loyalty: true };
    if (isReportPath) return { ...allGroupsClosed, reports: true };
    if (isDocsPath) return { ...allGroupsClosed, docs: true };
    if (isDisplayPath) return { ...allGroupsClosed, display: true };
    return allGroupsClosed;
  });
  useEffect(() => {
    if (isReportPath) setGroupExpanded({ ...allGroupsClosed, reports: true });
  }, [isReportPath]);
  useEffect(() => {
    if (isLoyaltyPath && isLoyaltyAllowed) setGroupExpanded({ ...allGroupsClosed, loyalty: true });
  }, [isLoyaltyPath, isLoyaltyAllowed]);
  useEffect(() => {
    if (isProductCatalogPath) setGroupExpanded({ ...allGroupsClosed, productCatalog: true });
  }, [isProductCatalogPath]);
  useEffect(() => {
    if (isMediaManagementPath) setGroupExpanded({ ...allGroupsClosed, mediaManagement: true });
  }, [isMediaManagementPath]);
  useEffect(() => {
    if (isCateringPath) setGroupExpanded({ ...allGroupsClosed, catering: true });
  }, [isCateringPath]);
  useEffect(() => {
    if (isDisplayPath) setGroupExpanded({ ...allGroupsClosed, display: true });
  }, [isDisplayPath]);
  useEffect(() => {
    if (isCampaignsGroupPath) setGroupExpanded({ ...allGroupsClosed, campaigns: true });
  }, [isCampaignsGroupPath]);
  useEffect(() => {
    if (isDocsPath) setGroupExpanded({ ...allGroupsClosed, docs: true });
  }, [isDocsPath]);
  const toggleGroup = (groupId: string) => setGroupExpanded((prev: any) => {
    const isCurrentlyOpen = prev[groupId];
    // Close all, then open the clicked one (if it was closed)
    return { ...allGroupsClosed, [groupId]: !isCurrentlyOpen };
  });

  const {
    logoLetters = 'M',
    brandName = 'Maghil',
    subtitle = 'ANALYTICS',
  } = brand;

  const outletLogoUrl = outlet?.logoUrl ?? null;
  const outletLocation = outlet?.location ?? '';
  const outletDisplayName = outlet?.name ?? outlet?.label ?? brandName;

  const hasOutletDropdown = outlet?.options != null && outlet.options.length > 0;
  const readOnlyOutletLabelOnly = outlet?.readOnlyLabelOnly === true;
  const outletOpen = outlet?.open ?? false;
  const outletToggle = outlet?.onToggle;
  // True until the selected outlet is resolved (name available). On native the
  // auth/branch storage hydrates asynchronously, so this stays true from the first
  // paint — we show shimmer in place of the logo + dropdown instead of flashing the
  // "Select outlet" placeholder and the fallback brand mark before data arrives.
  const outletDetailsLoading = outlet?.detailsLoading ?? false;

  useEffect(() => {
    if (!outletToggle || !outletOpen) return;
    const handleClickOutside = (e) => {
      if (outletRef.current && !outletRef.current.contains(e.target)) outletToggle();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [outletOpen, outletToggle]);

  const [userOpen, setUserOpen] = useState(false);
  const [outletLogoError, setOutletLogoError] = useState(false);
  useEffect(() => {
    setOutletLogoError(false);
  }, [outletLogoUrl]);

  useEffect(() => {
    if (!user?.onLogout) return;
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [user?.onLogout]);

  return (
    <aside
      style={{
        width: 250,
        minWidth: 250,
        background: T.surface,
        borderRight: `1px solid ${T.div}`,
        boxShadow: T.sbSh,
        position: 'relative',
        height: '100%',
        minHeight: 0,
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        fontFamily: T.f,
        zIndex: 2,
      }}
    >
      {/* Outlet profile header (logo, name, location) like Maghil portal */}
      {outlet ? (
        <div style={{ padding: 20, borderBottom: `1px solid ${T.div}`, textAlign: 'center' }}>
          <div
            style={{
              borderRadius: 0,
              margin: '0 auto 12px',
              overflow: 'hidden',
              // background: outletLogoUrl && !outletLogoError ? T.hover : 'linear-gradient(135deg,#D23A3B,#E86B6B)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              maxHeight: 64,
              width: 'auto',
            }}
          >
            {outletDetailsLoading && !outletLogoUrl ? (
              <Bone w={120} h={48} r={6} />
            ) : outletLogoUrl && !outletLogoError ? (
              <img
                src={outletLogoUrl}
                alt=""
                style={{ maxHeight: 64, width: 'auto', height: 'auto', objectFit: 'contain' }}
                onError={() => setOutletLogoError(true)}
              />
            ) : (
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 22 }}>
                {(logoLetters || outletDisplayName?.slice(0, 2) || 'M').toString().slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          {outletDetailsLoading ? (
            <Bone w={120} h={15} r={6} style={{ margin: '0 auto 4px' }} />
          ) : (
            <div style={{ fontSize: 15, fontWeight: 700, color: T.t1, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {outletDisplayName}
            </div>
          )}
          {outletDetailsLoading ? (
            <Bone w={80} h={11} r={6} style={{ margin: '0 auto 12px' }} />
          ) : outletLocation ? (
            <div style={{ fontSize: 12, color: T.t3, marginBottom: 12 }}>{outletLocation}</div>
          ) : null}
        </div>
      ) : (
        /* Logo / Brand when no outlet */
        <div style={{ padding: 20, borderBottom: `1px solid ${T.div}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: 'linear-gradient(135deg,#D23A3B,#E86B6B)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 700,
                fontSize: 16,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {(logoLetters || brandName?.slice(0, 2) || 'M').toString().slice(0, 2).toUpperCase()}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {brandName}
              </div>
              {subtitle && (
                <div style={{ fontSize: 10, color: T.t3, letterSpacing: 0.5 }}>{subtitle}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Outlet selector (dropdown to switch branches) */}
      {outlet && (
        <div ref={outletRef} style={{ padding: '12px 16px', position: 'relative' }}>
          {readOnlyOutletLabelOnly ? (
            <div className="sidebar-outlet-readonly-label">{outlet.label}</div>
          ) : hasOutletDropdown ? (
            <>
              <div
                onClick={outletToggle}
                style={{
                  background: T.hover,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 6,
                  cursor: 'pointer',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {outlet.label}
                </span>
                <Chev s={10} c={T.t3} r={outletOpen ? 180 : 0} />
              </div>
              {outletOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 16,
                    right: 16,
                    marginTop: 4,
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    boxShadow: T.sh,
                    maxHeight: 220,
                    overflow: 'auto',
                    zIndex: 100,
                  }}
                >
                  {outlet.loading ? (
                    <div style={{ padding: 12, fontSize: 12, color: T.t3 }}>Loading...</div>
                  ) : outlet.options.length === 0 ? (
                    <div style={{ padding: 12, fontSize: 12, color: T.t3 }}>No branches</div>
                  ) : (
                    outlet.options.map((opt) => {
                      const id = typeof opt === 'object' && opt !== null && 'id' in opt ? opt.id : opt;
                      const label = typeof opt === 'object' && opt !== null && 'label' in opt ? opt.label : String(opt);
                      const isSelected = id === outlet.selectedId;
                      return (
                        <div
                          key={id}
                          onClick={() => outlet.onSelect(opt)}
                          style={{
                            padding: '10px 12px',
                            fontSize: 12,
                            fontWeight: isSelected ? 600 : 500,
                            cursor: 'pointer',
                            background: isSelected ? T.accentSoft : 'transparent',
                            color: isSelected ? T.accent : T.t1,
                          }}
                        >
                          {label}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                background: T.hover,
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                padding: '8px 12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <span>{outlet.label}</span>
              <Chev s={10} c={T.t3} />
            </div>
          )}
        </div>
      )}

      {/* Nav */}
      <nav style={{ flex: 1, padding: '4px 8px', overflowY: 'auto' }}>
        {navItems.map((item) => {
          if (item.type === 'group') {
            const expanded = groupExpanded[item.id] !== false;
            const isActive = item.active;
            return (
              <div key={item.id} style={{ padding: '0 8px', marginBottom: 2 }}>
                <div
                  onClick={() => {
                    if (item.id === 'reports' && useBuiltNav) {
                      if (!expanded && !isReportPath) navigate('/reports/snapshot');
                      toggleGroup(item.id);
                      return;
                    }
                    if (item.id === 'productCatalog' && useBuiltNav) {
                      if (!expanded && !isProductCatalogPath) navigate(pcPaths.availability);
                      toggleGroup(item.id);
                      return;
                    }
                    if (item.id === 'loyalty' && useBuiltNav) {
                      if (!expanded && !isLoyaltyPath) navigate('/loyalty/program');
                      toggleGroup(item.id);
                      return;
                    }
                    if (item.id === 'catering' && useBuiltNav) {
                      // Lands on the first Catering page the employee can open (or Access Restricted) — see CateringLanding.
                      if (!expanded && !isCateringPath) navigate(cateringPaths.start);
                      toggleGroup(item.id);
                      return;
                    }
                    if (item.id === 'catering' && useBuiltNav) {
                      if (!expanded && !isCateringPath) navigate(cateringPaths.leads);
                      toggleGroup(item.id);
                      return;
                    }
                    if (item.id === 'campaigns' && useBuiltNav) {
                      if (!expanded && !isCampaignsGroupPath) navigate(campaignPaths.list);
                      toggleGroup(item.id);
                      return;
                    }
                    if (item.id === 'display' && useBuiltNav) {
                      if (!expanded && !isDisplayPath) navigate(displayPaths.root);
                      toggleGroup(item.id);
                      return;
                    }
                    if (item.id === 'mediaManagement' && useBuiltNav) {
                      if (!expanded && !isMediaManagementPath) navigate(mediaManagementPaths.location);
                      toggleGroup(item.id);
                      return;
                    }
                    toggleGroup(item.id);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '9px 10px',
                    cursor: 'pointer',
                    borderRadius: 6,
                    fontSize: 12.5,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? T.accent : T.t2,
                    background: isActive ? T.accentSoft : 'transparent',
                    borderLeft: isActive ? `2px solid ${T.accent}` : '2px solid transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  {item.icon && <MSIcon name={item.icon} size={18} color={isActive ? T.accent : T.t3} />}
                  <span style={{ flex: 1 }}>{item.label}</span>
                  <Chev s={10} c={isActive ? T.accent : T.t3} r={expanded ? 180 : 0} />
                </div>
                {expanded &&
                  (item.children || []).map((child) => {
                    const childActive = child.active;
                    const childDisabled = child.disabled;
                    const childClick = childDisabled ? undefined : child.onClick;
                    return (
                      <div key={child.id} style={{ padding: '0 8px', marginBottom: 2, paddingLeft: 24, opacity: childDisabled ? 0.4 : 1 }}>
                        <div
                          onClick={childClick}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '9px 10px',
                            cursor: childDisabled ? 'not-allowed' : childClick ? 'pointer' : 'default',
                            borderRadius: 6,
                            fontSize: 12.5,
                            fontWeight: childActive ? 600 : 400,
                            color: childActive ? T.accent : T.t2,
                            background: childActive ? T.accentSoft : 'transparent',
                            borderLeft: childActive ? `2px solid ${T.accent}` : '2px solid transparent',
                            transition: 'all 0.15s',
                          }}
                        >
                          {child.icon && <MSIcon name={child.icon} size={18} color={childActive ? T.accent : T.t3} />}
                          <span>{child.label}</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            );
          }
          const isActive = item.active;
          const handleClick = item.onClick ?? (item.path ? () => {} : undefined);
          return (
            <div key={item.id} style={{ padding: '0 8px', marginBottom: 2 }}>
              <div
                onClick={handleClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '9px 10px',
                  cursor: handleClick ? 'pointer' : 'default',
                  borderRadius: 6,
                  fontSize: 12.5,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? T.accent : T.t2,
                  background: isActive ? T.accentSoft : 'transparent',
                  borderLeft: isActive ? `2px solid ${T.accent}` : '2px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                {item.icon && <MSIcon name={item.icon} size={18} color={isActive ? T.accent : T.t3} />}
                <span>{item.label}</span>
              </div>
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      {user && (
        <div ref={userMenuRef} style={{ padding: '16px 20px', borderTop: `1px solid ${T.div}`, position: 'relative' }}>
          {user.onLogout ? (
            <>
              <div
                onClick={() => setUserOpen(!userOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  borderRadius: 8,
                  padding: '4px 0',
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 14,
                    background: T.hover,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    color: T.t2,
                  }}
                >
                  {user.initials}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.name}
                  </div>
                  <div style={{ fontSize: 10, color: T.t3 }}>{user.role}</div>
                </div>
                <Chev s={10} c={T.t3} r={userOpen ? 180 : 0} />
              </div>
              {userOpen && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 20,
                    right: 20,
                    marginBottom: 4,
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    boxShadow: T.sh,
                    zIndex: 100,
                    overflow: 'hidden',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => { user.onLogout(); setUserOpen(false); }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      fontSize: 12,
                      fontWeight: 600,
                      color: T.red,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  background: T.hover,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  color: T.t2,
                }}
              >
                {user.initials}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{user.name}</div>
                <div style={{ fontSize: 10, color: T.t3 }}>{user.role}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
